if (!customElements.get('product-info')) {
  customElements.define('product-info', class ProductInfo extends HTMLElement {

    connectedCallback() {
      this.currentVariant = this.getSelectedVariant();
      this.addEventListener('change', this.onVariantChange.bind(this));
      // DO NOT call updateVariantStatuses() here — Liquid renders correct initial state.
      // Only update after user interaction.
    }

    onVariantChange() {
      this.updateOptions();
      this.updateMasterId();
      this.updateVariantInput();
      this.updateURL();
      this.updateVariantStatuses();
      this.renderProductInfo();
      this.updateMedia();
      // Notify gallery (and any other listeners) which variant is now active.
      // The gallery uses this to scroll to the variant's featured image.
      this.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: this.currentVariant }
      }));
    }

    // ── Collect currently selected option values in option-group order ──────
    updateOptions() {
      const names = this._optionNames();
      if (names.length) {
        // Radio buttons
        this.options = names.map(name => {
          const el = this.querySelector('.product-form__options input[type="radio"][name="' + name + '"]:checked');
          return el ? el.value : '';
        });
      } else {
        // Dropdown selects
        this.options = Array.from(this.querySelectorAll('select')).map(s => s.value);
      }
    }

    // Returns unique radio-input name attributes in DOM order
    _optionNames() {
      const seen = [], names = [];
      this.querySelectorAll('.product-form__options input[type="radio"]').forEach(inp => {
        if (!seen.includes(inp.name)) { seen.push(inp.name); names.push(inp.name); }
      });
      return names;
    }

    updateMasterId() {
      const variants = this.getVariantsData();
      this.currentVariant = variants.find(v =>
        v.options.every((opt, i) => opt === this.options[i])
      ) || null;
    }

    updateVariantInput() {
      if (!this.currentVariant) return;
      this.querySelectorAll('input[name="id"]').forEach(inp => {
        inp.value = this.currentVariant.id;
      });
    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', this.dataset.url + '?variant=' + this.currentVariant.id);
    }

    // ── Mark option values as unavailable based on current selections ───────
    updateVariantStatuses() {
      const variants = this.getVariantsData();
      if (!variants.length) return;           // no data → leave DOM alone
      const sel = this.options || [];
      const names = this._optionNames();

      this.querySelectorAll('.product-form__option').forEach(opt => {
        const inp = opt.querySelector('input[type="radio"]');
        if (!inp) return;
        const val = inp.value;
        const idx = names.indexOf(inp.name);
        if (idx < 0) return;

        // Available = at least one variant with this value is available
        // while respecting all OTHER currently selected options
        const avail = variants.some(v => {
          if (!v.available) return false;
          if (v.options[idx] !== val) return false;
          for (let i = 0; i < sel.length; i++) {
            if (i === idx) continue;
            if (sel[i] && v.options[i] !== sel[i]) return false;
          }
          return true;
        });

        const lbl = opt.querySelector('.product-form__option-label');
        if (lbl) lbl.classList.toggle('product-form__option-label--unavailable', !avail);
        // Do NOT set inp.disabled — only the CSS class controls the visual strikethrough
      });
    }

    renderProductInfo() {
      this.updatePrice();
      this.updateBuyButton();
      this.updateSelectedLabel();
    }

    updatePrice() {
      const pc = this.querySelector('[data-price]');
      if (!pc || !this.currentVariant) return;
      const v = this.currentVariant;
      const pr = pc.querySelector('.price-item--sale, .price-item--regular');
      const cp = pc.querySelector('.price-item--compare');
      const badge = pc.querySelector('.price__badge');
      if (pr) pr.textContent = this.fmt(v.price);
      if (v.compare_at_price && v.compare_at_price > v.price) {
        pc.classList.add('price--on-sale');
        if (cp) { cp.textContent = this.fmt(v.compare_at_price); cp.hidden = false; }
        if (badge) {
          const pct = Math.round((1 - v.price / v.compare_at_price) * 100);
          const bt = badge.querySelector('.price__badge-text');
          if (bt) bt.textContent = 'Sale -' + pct + '%';
          badge.hidden = false;
        }
      } else {
        pc.classList.remove('price--on-sale');
        if (cp) cp.hidden = true;
        if (badge) badge.hidden = true;
      }
      pc.classList.toggle('price--sold-out', !v.available);
    }

    updateBuyButton() {
      const btn = this.querySelector('.product-form__submit');
      if (!btn) return;
      const txt = btn.querySelector('.product-form__submit-text');
      const vs = window.variantStrings || {};
      if (this.currentVariant && this.currentVariant.available) {
        btn.disabled = false;
        if (txt) txt.textContent = vs.addToCart || 'Add to cart';
      } else if (this.currentVariant) {
        btn.disabled = true;
        if (txt) txt.textContent = vs.soldOut || 'Sold out';
      } else {
        btn.disabled = true;
        if (txt) txt.textContent = vs.unavailable || 'Unavailable';
      }
    }

    updateSelectedLabel() {
      const names = this._optionNames();
      names.forEach(name => {
        const checked = this.querySelector('input[type="radio"][name="' + name + '"]:checked');
        if (!checked) return;
        const fieldset = checked.closest('fieldset');
        if (!fieldset) return;
        const lbl = fieldset.querySelector('[data-option-value]');
        if (lbl) lbl.textContent = checked.value;
      });
    }

    // ── Media swap ───────────────────────────────────────────────────────────
    updateMedia() {
      if (!this.currentVariant || !this.currentVariant.featured_media) return;
      const gallery = document.querySelector('[data-media-gallery]');
      if (!gallery) return;
      const tid = String(this.currentVariant.featured_media.id);
      gallery.querySelectorAll('[data-media-id]').forEach(item => {
        const active = item.dataset.mediaId === tid;
        item.classList.toggle('product__media-item--active', active);
        if (active) item.removeAttribute('hidden');
        else item.setAttribute('hidden', '');
      });
    }

    // ── Helpers ───────────────────────────────────────────────────────────────
    fmt(cents) {
      try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
      } catch (e) {
        return '$' + (cents / 100).toFixed(2);
      }
    }

    getSelectedVariant() {
      const el = this.querySelector('[data-selected-variant]');
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }

    getVariantsData() {
      const pid = this.dataset.productId;
      // Primary: <script id="kb-variants-{id}"> injected by main-product.liquid
      if (pid) {
        const tag = document.getElementById('kb-variants-' + pid);
        if (tag) { try { return JSON.parse(tag.textContent); } catch (e) {} }
      }
      // Fallback: window.productVariants (older injection)
      if (pid && window.productVariants && window.productVariants[pid]) {
        return window.productVariants[pid];
      }
      return [];
    }
  });
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', class VariantSelects extends HTMLElement {});
}
